import { get, post } from '../request';
import type {
  ApiUsageStats,
  ApiUsageHistory,
  RateLimitStatus,
  PerformanceMetrics,
  QueueStatus,
  HealthCheckResult,
  ServiceAvailability,
  AlertRule,
  ActiveAlert,
  AlertActionResult,
  AuditLog,
  ErrorLog,
  ResponseTimeStats,
  ThroughputStats,
  ErrorRateStats,
  SlowQuery,
  SystemResources,
  ApiCallHistory,
  ApiCallsByEndpoint,
  ApiQuotaHistory,
  ApiUsageReportExport,
  PerformanceGrade,
  RecentAlert,
  HealthCheckStatus
} from './monitoring.d';


/**
 * Amazon监控统计API服务
 */

// ============= API使用统计 =============

/**
 * 获取API使用统计
 */
export async function getApiUsageStats(params?: { start_date?: string; end_date?: string; credential_id?: number; date_range?: any[]; }) {
  return get<ApiUsageStats>('/api/tenant/amazon/api-usage', params);
}

/**
 * 获取详细API使用历史
 */
export async function getApiUsageHistory(params?: {
  start_date?: string;
  end_date?: string;
  api_type?: string;
  group_by?: 'hour' | 'day' | 'week';
}) {
  return get<ApiUsageHistory>('/api/tenant/amazon/api-usage/history', params);
}

/**
 * 获取限流状态
 */
export async function getRateLimitStatus() {
  return get<RateLimitStatus>('/api/tenant/amazon/rate-limits');
}

// ============= 性能监控 =============

/**
 * 获取系统性能指标
 */
export async function getPerformanceMetrics(params?: { start_date?: string; end_date?: string; date_range?: any[]; endpoint?: string; }) {
  return get<PerformanceMetrics>('/api/tenant/amazon/performance', params);
}

/**
 * 获取队列状态
 */
export async function getQueueStatus() {
  return get<QueueStatus>('/api/tenant/amazon/queue-status');
}

// ============= 健康检查 =============

/**
 * 执行系统健康检查
 */
export async function performHealthCheck() {
  return post<HealthCheckResult>('/api/tenant/amazon/health-check');
}

/**
 * 获取服务可用性状态
 */
export async function getServiceAvailability() {
  return get<ServiceAvailability>('/api/tenant/amazon/availability');
}

// ============= 告警和通知 =============

/**
 * 获取告警规则
 */
export async function getAlertRules() {
  return get<AlertRule[]>('/api/tenant/amazon/alerts/rules');
}

/**
 * 获取活跃告警
 */
export async function getActiveAlerts() {
  return get<ActiveAlert[]>('/api/tenant/amazon/alerts/active');
}

/**
 * 确认告警
 */
export async function acknowledgeAlert(alertId: number) {
  return post<AlertActionResult>(`/api/tenant/amazon/alerts/${alertId}/acknowledge`);
}

/**
 * 解决告警
 */
export async function resolveAlert(alertId: number, resolution?: string) {
  return post<AlertActionResult>(`/api/tenant/amazon/alerts/${alertId}/resolve`, { resolution });
}

// ============= 日志和审计 =============

/**
 * 获取操作日志
 */
export async function getAuditLogs(params?: {
  current?: number;
  pageSize?: number;
  action_type?: string;
  user_id?: number;
  start_date?: string;
  end_date?: string;
}) {
  return get<AuditLog[]>('/api/tenant/amazon/audit-logs', params);
}

/**
 * 获取错误日志
 */
export async function getErrorLogs(params?: {
  current?: number;
  pageSize?: number;
  level?: 'error' | 'warning' | 'info';
  category?: string;
  start_date?: string;
  end_date?: string;
}) {
  return get<ErrorLog[]>('/api/tenant/amazon/error-logs', params);
}

// ============= 缺失的API函数 =============

/**
 * 获取健康检查状态
 */
export async function getHealthCheckStatus() {
  return get<HealthCheckStatus>('/api/tenant/amazon/health-check');
}

/**
 * 获取最近告警
 */
export async function getRecentAlerts(params?: {
  limit?: number;
  severity?: string;
  status?: string;
}) {
  return get<RecentAlert[]>('/api/tenant/amazon/alerts/recent', params);
}

/**
 * 获取API调用历史
 */
export async function getApiCallHistory(params?: {
  start_date?: string;
  end_date?: string;
  endpoint?: string;
  status?: string;
  limit?: number;
  credential_id?: number;
  date_range?: any[];
  page?: number;
  per_page?: number;
}) {
  return get<ApiCallHistory[]>('/api/tenant/amazon/api-calls/history', params);
}

/**
 * 获取按端点分组的API调用统计
 */
export async function getApiCallsByEndpoint(params?: {
  start_date?: string;
  end_date?: string;
  group_by?: 'hour' | 'day';
  credential_id?: number;
  date_range?: any[];
}) {
  return get<ApiCallsByEndpoint[]>('/api/tenant/amazon/api-calls/by-endpoint', params);
}

/**
 * 获取API配额历史
 */
export async function getApiQuotaHistory(params?: {
  start_date?: string;
  end_date?: string;
  api_type?: string;
  credential_id?: number;
  date_range?: any[];
}) {
  return get<ApiQuotaHistory[]>('/api/tenant/amazon/api-quota/history', params);
}

/**
 * 导出API使用报告
 */
export async function exportApiUsageReport(params: {
  start_date: string;
  end_date: string;
  format: 'csv' | 'excel' | 'pdf';
  include_details?: boolean;
}) {
  return post<ApiUsageReportExport>('/api/tenant/amazon/api-usage/export', params);
}

/**
 * 获取响应时间统计
 */
export async function getResponseTimeStats(params?: {
  start_date?: string;
  end_date?: string;
  endpoint?: string;
  date_range?: any[];
}) {
  return get<ResponseTimeStats>('/api/tenant/amazon/performance/response-time', params);
}

/**
 * 获取吞吐量统计
 */
export async function getThroughputStats(params?: {
  start_date?: string;
  end_date?: string;
  date_range?: any[];
}) {
  return get<ThroughputStats>('/api/tenant/amazon/performance/throughput', params);
}

/**
 * 获取错误率统计
 */
export async function getErrorRateStats(params?: {
  start_date?: string;
  end_date?: string;
  error_type?: string;
  date_range?: any[];
}) {
  return get<ErrorRateStats>('/api/tenant/amazon/performance/error-rate', params);
}

/**
 * 获取慢查询统计
 */
export async function getSlowQueries(params?: {
  start_date?: string;
  end_date?: string;
  threshold?: number;
  limit?: number;
  date_range?: any[];
}) {
  return get<SlowQuery[]>('/api/tenant/amazon/performance/slow-queries', params);
}

/**
 * 获取系统资源统计
 */
export async function getSystemResources(params?: {
  start_date?: string;
  end_date?: string;
}) {
  return get<SystemResources>('/api/tenant/amazon/performance/system-resources', params);
}
